import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { API_PATHS } from '../config/api-paths';
import { OfficerPreferences, OfficerPreferencesService } from '../tokens/officer-preferences.token';

/**
 * REST-only implementation of {@link OfficerPreferencesService} (EM-1295 / epic EM-1283; renamed
 * role-neutral by EM-1471, epic EM-1447 Story 2).
 *
 * ```
 * GET /services/embedded/api/kyc/preferences
 * PUT /services/embedded/api/kyc/preferences
 * ```
 *
 * The path deliberately keeps its `kyc` segment — a live wire contract; only the portal-side names
 * changed. Backed by `KycOfficerSettingsResource`, which delegates to the same `AdminPreferenceService`
 * the master-admin settings page uses — one login-keyed row, two field-disjoint write paths. Both the
 * KYC and the Underwriting workspace Settings sections consume this one service.
 *
 * Rules 07 / 10 — no mock fallback, no `Mock*` sibling.
 *
 * Rule 09 — errors are deliberately **not** mapped here. The RFC 7807 body reaches the caller
 * untouched so the Settings section can run it through `extractBackendErrorMessage(err, fallback)`
 * with a *translated* fallback; resolving it here would force this service to either inject Transloco
 * or emit a raw i18n key as if it were display text.
 */
@Injectable()
export class OfficerPreferencesRestService implements OfficerPreferencesService {
  private readonly http = inject(HttpClient);

  private readonly url = API_PATHS.OFFICER_PREFERENCES;

  getPreferences(): Observable<OfficerPreferences> {
    return this.http.get<OfficerPreferences>(this.url);
  }

  updatePreferences(prefs: OfficerPreferences): Observable<OfficerPreferences> {
    return this.http.put<OfficerPreferences>(this.url, prefs);
  }
}
